/**
 * useCreateCard — TanStack Query mutation hook factory for creating a
 * deliverable of a given kind.
 *
 *   const createCard = useCreateCard('task', { projectId })
 *   await createCard.mutateAsync({ statusId, title, ... })
 *
 * The endpoint maps 1:1 from `kind` to `/api/${kind}s` (BE convention:
 * tasks, tickets, events, announcements). Each BE create-route returns
 * the new wrapped envelope `{ data: <kind>Object }` (BE-2 migration).
 *
 * No optimistic update — the BE assigns the row's `id`, `position`,
 * and computed timestamps, so an optimistic write would have to fake
 * data we don't own. Instead we invalidate `['board', projectId]` on
 * success so `useBoard` refetches and the new card lands in the right
 * column.
 *
 * Per-kind request shapes mirror the BE Zod input schemas in
 * `3mote-be-new/src/schemas/deliverables/`. Hook-local types here
 * (Phase 2c codegen replaces them).
 */

import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query'

import { apiClient } from '@/api/client'
import type { DeliverableKind, RecurrenceInput, TipTapJsonContent } from '@/api/types-bridge'
import { CARD_LIST_QUERY_ROOT } from '@/features/board/components/list/use-card-list'
import { BOARD_QUERY_KEY } from '@/features/board/use-board'
import { DASHBOARD_QUERY_KEY } from '@/features/dashboard/use-dashboard'

type Priority = 'low' | 'medium' | 'high' | 'urgent'
type Severity = 'low' | 'medium' | 'high' | 'urgent'

export interface CreateTaskRequest {
  projectId: string
  statusId: string
  title: string
  /**
   * Rich-text body — dual-format per ADR-039 Phase 1: TipTap JSON doc
   * (canonical, what the create modal emits) or legacy markdown string.
   */
  description?: string | TipTapJsonContent
  priority?: Priority
  assigneeId?: string | null
  dueAt?: string | null
  /**
   * MOTE-123 — optional recurrence rule applied in the same tx as the
   * task insert. Omit / undefined for non-recurring tasks.
   */
  recurrence?: RecurrenceInput | null
  /**
   * Ids of files uploaded two-phase (presigned without a parent) that
   * the create route attaches to the new card in the same transaction.
   * Sourced from the description-field OS-file drop + toolbar image
   * upload. The BE create routes accept + attach these in parallel.
   */
  attachmentIds?: string[]
}

export interface CreateTicketRequest {
  projectId: string
  statusId: string
  /**
   * Optional; the BE auto-generates a shorthand (e.g. `MOTE-101`) on
   * create. The legacy free-text input was dropped in Wave 1 of the
   * legacy parity sweep — leaving this optional preserves wire-compat
   * for any caller that still wants to override.
   */
  shorthand?: string
  title: string
  /** Rich-text body — dual-format per ADR-039 Phase 1 (see CreateTaskRequest). */
  description?: string | TipTapJsonContent
  priority?: Priority
  severity?: Severity
  assigneeId?: string | null
  reporter: string
  dueAt?: string | null
  /** See `CreateTaskRequest.attachmentIds`. */
  attachmentIds?: string[]
}

/**
 * Wave 4 Batch K — events accept `attendeeIds` instead of `assigneeId`
 * and the legacy single-host model is gone. The creator is BE-stamped
 * from the auth token; the attendee set is what the modal collects via
 * `<UserMultiPicker>`.
 */
export interface CreateEventRequest {
  projectId: string
  statusId: string
  title: string
  /** Rich-text body — dual-format per ADR-039 Phase 1 (see CreateTaskRequest). */
  description?: string | TipTapJsonContent
  startAt: string
  endAt?: string | null
  /** Provider-agnostic meeting URL (http/https). Empty clears to null. */
  meetingUrl?: string | null
  attendeeIds?: string[]
  /** See `CreateTaskRequest.attachmentIds`. */
  attachmentIds?: string[]
  /**
   * MOTE-123 — optional recurrence rule. For events the `on_completion`
   * trigger is effectively a no-op (events don't pass through the move
   * route — column is server-derived from start/end times); recurring
   * events should use `triggerType: 'on_date'`, which fires via the
   * lazy on-read scan on every board / dashboard read.
   */
  recurrence?: RecurrenceInput | null
}

/**
 * Wave 4 Batch K — announcements are tenant-wide. `projectId` is now
 * optional; when omitted (or null), the BE treats the announcement as
 * org-wide and surfaces it in every project's board AND on the master
 * dashboard. `attendeeIds` replaces `assigneeId` for the same reason
 * events did.
 */
export interface CreateAnnouncementRequest {
  /** Optional — omit / null for tenant-wide announcements. */
  projectId?: string
  /**
   * Optional — when `projectId` is omitted, the BE picks the
   * appropriate per-project status during the tenant-wide fanout, so
   * the FE can submit without a status either.
   */
  statusId?: string
  title: string
  /** Rich-text body — dual-format per ADR-039 Phase 1 (see CreateTaskRequest). */
  body?: string | TipTapJsonContent
  publishedAt?: string
  /**
   * One or more date rows describing WHEN the announcement is in
   * effect. Per legacy parity (David's directive), announcements
   * never expose a free-form "Published at" — they always have at
   * least one date row. The on-read coercion in
   * `deriveAnnouncementStatusName(dates, now)` uses these to bucket
   * the announcement into Inbox / Up Next / Today / Done. Without
   * them the row is permanently stuck in Inbox.
   */
  dates?: Array<{
    entryType: 'time' | 'duration'
    date: string
    startTime?: string | null
    endTime?: string | null
    hours?: number | null
    minutes?: number | null
    position?: number
  }>
  attendeeIds?: string[]
  /** See `CreateTaskRequest.attachmentIds`. */
  attachmentIds?: string[]
}

/**
 * Per-kind request map. Splitting this lets TS give callers the right
 * shape for `mutateAsync` based on the `kind` they pass in.
 */
export interface CreateCardRequestByKind {
  task: CreateTaskRequest
  ticket: CreateTicketRequest
  event: CreateEventRequest
  announcement: CreateAnnouncementRequest
}

/**
 * Per-kind response wrap. The created object's exact shape varies per
 * kind (the BE returns a serialized row of the appropriate table); the
 * FE only needs the discriminated `id` to navigate or log, so we keep
 * the wrap typed but the inner record loose. When OpenAPI codegen lands
 * the unknowns here become the typed Task/Ticket/Event/Announcement
 * DTOs.
 */
export interface CreateCardResponse {
  data: { id: string } & Record<string, unknown>
}

const PATH_BY_KIND: Record<DeliverableKind, string> = {
  task: '/api/tasks',
  ticket: '/api/tickets',
  event: '/api/events',
  announcement: '/api/announcements',
}

interface UseCreateCardArgs {
  projectId: string
}

export function useCreateCard<K extends DeliverableKind>(
  kind: K,
  { projectId }: UseCreateCardArgs,
): UseMutationResult<CreateCardResponse['data'], Error, CreateCardRequestByKind[K]> {
  const queryClient = useQueryClient()
  const path = PATH_BY_KIND[kind]

  return useMutation<CreateCardResponse['data'], Error, CreateCardRequestByKind[K]>({
    mutationFn: async (input) => {
      const res = await apiClient<CreateCardResponse>(path, {
        method: 'POST',
        body: input,
      })
      return res.data
    },
    onSuccess: () => {
      // Invalidate the per-project board AND the master dashboard so a
      // newly-created card lands on every open surface (a tenant-wide
      // announcement created from any project's board appears on all
      // boards + master without a refresh).
      queryClient.invalidateQueries({ queryKey: BOARD_QUERY_KEY(projectId) })
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: CARD_LIST_QUERY_ROOT })
    },
  })
}
